/* eslint-disable no-unused-vars */
import React, { useRef, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Divider,
} from "@mui/material";
import ContactForm from "../components/ContactForm";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import sample1 from "../assets/images/sample1.jpeg";
import "leaflet/dist/leaflet.css";

// Pure JS Leaflet Map Component
const MapComponent = () => {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  useEffect(() => {
    // Only run on client-side
    if (typeof window !== "undefined") {
      // Dynamically import Leaflet to avoid SSR issues
      import("leaflet").then((L) => {
        // Clean up existing map instance if it exists
        if (mapRef.current) {
          mapRef.current.remove();
        }

        // Create map only if the container exists
        if (mapContainerRef.current) {
          // Initialize map
          mapRef.current = L.map(mapContainerRef.current).setView(
            [42.2473, -71.9091],
            15
          );

          // Add tile layer
          L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          }).addTo(mapRef.current);

          // Add marker with popup
          L.marker([42.2473, -71.9091])
            .addTo(mapRef.current)
            .bindPopup(
              `
              <strong>PAT Electric</strong><br>
              121 Marshall St<br>
              Leicester, MA 01524<br>
              United States
            `
            )
            .openPopup();
        }
      });
    }

    // Cleanup function to remove the map when component unmounts
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, []);

  return (
    <div
      ref={mapContainerRef}
      style={{ height: "400px", width: "100%", borderRadius: "4px" }}
      className="leaflet-container"
    />
  );
};

const Contact = () => {
  const contactInfo = [
    {
      icon: <LocationOnIcon fontSize="large" className="text-gray-700 py-4" />,
      title: "Our Location",
      details: ["121 Marshall St", "Leicester, MA 01524", "United States"],
    },
    // {
    //   icon: <PhoneIcon fontSize="large" className="text-gray-700 py-4" />,
    //   title: 'Phone Numbers',
    //   details: [
    //     'Main: (555) 123-4567',
    //     'Service: (555) 123-4568',
    //     'Emergency: (555) 123-4569'
    //   ]
    // },
    // {
    //   icon: <EmailIcon fontSize="large" className="text-gray-700 py-4" />,
    //   title: 'Email Addresses',
    //   details: [
    //     'info@powerproelectric.com',
    //     'service@powerproelectric.com',
    //     'quotes@powerproelectric.com'
    //   ]
    // },
    {
      icon: <AccessTimeIcon fontSize="large" className="text-gray-700 py-4" />,
      title: "Working Hours",
      details: [
        "Monday-Friday: 8:00 AM - 6:00 PM",
        "Saturday: 9:00 AM - 3:00 PM",
        "Sunday: Closed (Emergency Service Available)",
      ],
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        className="py-20 text-white text-center"
        sx={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${sample1})`,
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h1" className="font-bold mb-4">
            Contact Us
          </Typography>
          <Typography variant="h6" className="max-w-2xl mx-auto">
            Get in touch with our team for all your electrical service needs.
          </Typography>
        </Container>
      </Box>

      {/* Contact Information */}
      <Box className="py-16">
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {contactInfo.map((info, index) => (
              <Grid item xs={12} sm={6} md={6} lg={5} key={index}>
                <Paper
                  elevation={2}
                  className="p-6 h-full text-center hover:shadow-lg transition-shadow duration-300"
                >
                  <Box className="flex justify-center mb-4">{info.icon}</Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    className="font-bold mb-3"
                  >
                    {info.title}
                  </Typography>
                  {info.details.map((detail, idx) => (
                    <Typography key={idx} variant="body2" className="mb-1">
                      {detail}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Form and Map */}
      <Box className="py-16 bg-gray-50">
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                component="h2"
                className="font-bold mb-6"
              >
                Send Us a Message
              </Typography>
              <ContactForm />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                component="h2"
                className="font-bold mb-6"
              >
                Our Location
              </Typography>
              <Paper elevation={3} className="overflow-hidden">
                <MapComponent />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section and Map */}
      <Box className="py-16">
        <Container maxWidth="lg">
          <Box className="text-center mb-12">
            <Typography variant="h4" component="h2" className="font-bold mb-3">
              Frequently Asked Questions
            </Typography>
            <Typography
              variant="body1"
              className="text-gray-600 max-w-2xl mx-auto"
            >
              Find answers to common questions about our electrical services.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} className="p-6 mb-4">
                <Typography
                  variant="h6"
                  component="h3"
                  className="font-bold mb-2"
                >
                  Do you offer emergency electrical services?
                </Typography>
                <Divider className="my-3" />
                <Typography variant="body2" className="text-gray-700">
                  Yes, we provide 24/7 emergency electrical services. For urgent
                  electrical issues, please call our emergency line at (555)
                  123-4569.
                </Typography>
              </Paper>

              <Paper elevation={1} className="p-6 mb-4">
                <Typography
                  variant="h6"
                  component="h3"
                  className="font-bold mb-2"
                >
                  Are your electricians licensed and insured?
                </Typography>
                <Divider className="my-3" />
                <Typography variant="body2" className="text-gray-700">
                  Absolutely. All our electricians are fully licensed, insured,
                  and bonded. We maintain all necessary certifications and
                  regularly participate in continuing education.
                </Typography>
              </Paper>

              <Paper elevation={1} className="p-6">
                <Typography
                  variant="h6"
                  component="h3"
                  className="font-bold mb-2"
                >
                  How quickly can you start a project?
                </Typography>
                <Divider className="my-3" />
                <Typography variant="body2" className="text-gray-700">
                  Our response time varies depending on our current workload and
                  the scope of your project. For small jobs, we can often
                  schedule within a few days. Larger projects might require more
                  planning, but we always strive to accommodate our clients'
                  timelines.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={1} className="p-6 mb-4">
                <Typography
                  variant="h6"
                  component="h3"
                  className="font-bold mb-2"
                >
                  Do you provide free estimates?
                </Typography>
                <Divider className="my-3" />
                <Typography variant="body2" className="text-gray-700">
                  Yes, we offer free estimates for most electrical projects. You
                  can request a quote by filling out our contact form, calling
                  our office, or sending us an email.
                </Typography>
              </Paper>

              <Paper elevation={1} className="p-6 mb-4">
                <Typography
                  variant="h6"
                  component="h3"
                  className="font-bold mb-2"
                >
                  What types of payment do you accept?
                </Typography>
                <Divider className="my-3" />
                <Typography variant="body2" className="text-gray-700">
                  We accept cash, checks, and all major credit cards. For larger
                  projects, we can discuss payment plans or milestone-based
                  payment schedules.
                </Typography>
              </Paper>

              <Paper elevation={1} className="p-6">
                <Typography
                  variant="h6"
                  component="h3"
                  className="font-bold mb-2"
                >
                  Do you offer warranties on your work?
                </Typography>
                <Divider className="my-3" />
                <Typography variant="body2" className="text-gray-700">
                  Yes, we stand behind our work with a comprehensive warranty.
                  All our labor is guaranteed for one year from the completion
                  date, and many of the products we install come with
                  manufacturer warranties as well.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Contact;
